import React, { useEffect, useState } from "react";
import { Button, Typography, Stack, Box, FormHelperText } from "@mui/material";
import service from "services/constant";

const CustomFileInput = ({
  onChange,
  label = "Choose File",
  name,
  value = [],
  multiple = false,
  error,
  helperText,
  isReadOnly = false,
  isImageinLink = false,
}) => {
  const [previewUrls, setPreviewUrls] = useState([]);

  const handleFileChange = (e) => {
    if (isReadOnly) return;

    const files = multiple ? Array.from(e.target.files) : [e.target.files[0]];

    const urls = files
      .filter((file) => file instanceof File || file instanceof Blob)
      .map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

    previewUrls.forEach(({ url }) => URL.revokeObjectURL(url));

    setPreviewUrls(urls);
    onChange({ target: { name, value: multiple ? files : files[0] } });
  };

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    const loadPreviews = async () => {
      const toBlobUrl = async (url) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch (err) {
          console.error("Failed to fetch blob from URL:", url, err);
          return url; // fallback to raw URL
        }
      };

      if (Array.isArray(value)) {
        const urls = await Promise.all(
          value.map(async (file) => {
            if (file instanceof File || file instanceof Blob) {
              return { file, url: URL.createObjectURL(file) };
            } else if (file?.imagePath) {
              return {
                file,
                // url: await toBlobUrl(file.imagePath),
                url: `${service.IMG_URL}${file?.imagePath || file?.imgUrl}`,
              };
            } else if (file?.imgUrl) {
              return {
                file,
                url: await toBlobUrl(file.imgUrl),
              };
            } else if (typeof file === "string") {
              return {
                file,
                url: await toBlobUrl(file),
              };
            }
            return null;
          })
        );
        setPreviewUrls(urls.filter(Boolean));
      } else if (value instanceof File || value instanceof Blob) {
        setPreviewUrls([{ file: value, url: URL.createObjectURL(value) }]);
      } else if (value && typeof value === "object") {
        let backendUrl = `${service.IMG_URL}${
          value?.imagePath || value?.imgUrl
        }`;
        if (backendUrl) {
          const blobUrl = await toBlobUrl(backendUrl);
          setPreviewUrls([{ file: value, url: blobUrl }]);
        }
      } else if (typeof value === "string") {
        const blobUrl = await toBlobUrl(value);
        setPreviewUrls([{ file: value, url: blobUrl }]);
      }
    };

    loadPreviews();

    // Cleanup on unmount or when value changes
    return () => {
      previewUrls.forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [value]);

  return (
    <Stack spacing={1}>
      {!isReadOnly && !isImageinLink && (
        <Button
          variant="outlined"
          component="label"
          disabled={isReadOnly}
          sx={{
            width: "100%",
            borderColor: error ? "error.main" : undefined,
            color: error ? "error.main" : undefined,
            cursor: isReadOnly ? "not-allowed" : "pointer",
            opacity: isReadOnly ? 0.6 : 1,
          }}
        >
          {label}
          <input
            hidden
            type="file"
            name={name}
            onChange={handleFileChange}
            multiple={multiple}
          />
        </Button>
      )}

      {error && helperText && (
        <FormHelperText error>{helperText}</FormHelperText>
      )}

      {previewUrls?.length > 0 && (
        <Stack spacing={2}>
          {previewUrls.map(({ file, url }, index) => {
            const isImage =
              (file?.type && file?.type?.startsWith("image/")) ||
              (typeof file?.imagePath === "string" &&
                /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file?.imagePath));

            // const isPDF = file?.type === "application/pdf";
            const isPDF =
              file?.type === "application/pdf" ||
              (typeof file?.imagePath === "string" &&
                /\.(pdf)$/i.test(file?.imagePath));

            let fileName = file?.name || file?.imagePath?.split("/").pop();

            return (
              <Box key={index}>
                {isImageinLink ? (
                  <>
                    {fileName?.length > 0 ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "underline" }}
                      >
                        {`${index + 1}. ${fileName}`}
                      </a>
                    ) : (
                      "NA"
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      <strong>
                        {file?.name || file?.imagePath?.split("/").pop()}
                      </strong>
                    </Typography>

                    {isImage && (
                      <Box
                        component="img"
                        src={url}
                        alt={`preview-${index}`}
                        sx={{
                          width: "100%",
                          maxWidth: 250,
                          borderRadius: 1,
                          mt: 1,
                        }}
                      />
                    )}

                    {isPDF && (
                      <Box
                        component="iframe"
                        src={url}
                        title={`pdf-preview-${index}`}
                        sx={{
                          width: "100%",
                          height: 300,
                          mt: 1,
                          border: "1px solid #ccc",
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default CustomFileInput;
